import { ADMIN_NAV_ITEMS } from '../constants/admin_nav_constants';
import { hasScope } from './scope_helpers';

export const MAX_RECENTS = 8;

// Items the current admin can see, in registry order.
export const getVisibleNavItems = scopes => ADMIN_NAV_ITEMS.filter(item => !item.scopes.length || hasScope(scopes, ...item.scopes));

// The nav item whose path best matches the current location (longest prefix wins).
export const getActiveNavKey = pathname => {
  const match = ADMIN_NAV_ITEMS.filter(item => pathname === item.path || pathname.startsWith(`${item.path}/`)).sort(
    (a, b) => b.path.length - a.path.length
  )[0];
  return match?.key || null;
};

// Drop keys the admin can no longer see (scopes changed) and keys for removed nav items.
export const keepVisibleKeys = (keys, visibleItems) => {
  const visible = new Set(visibleItems.map(item => item.key));
  return keys.filter(key => visible.has(key));
};

// Pinned/recent are persisted per-user as comma-joined slug strings under UserSettings.uiFlags.
// Nav keys are kebab-case slugs (never contain commas), so a join/split round-trips safely.
export const serializeNavKeys = keys => keys.join(',');
const parseNavKeys = value => (typeof value === 'string' && value ? value.split(',').filter(Boolean) : []);

// uiFlags comes back from the API as a raw JSON string (the column isn't parsed server-side), so parse it.
const toUiFlagsObject = uiFlags => {
  if (uiFlags && typeof uiFlags === 'object') return uiFlags;
  try {
    const parsed = JSON.parse(uiFlags || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const getNavPreferencesFromUiFlags = uiFlags => {
  const flags = toUiFlagsObject(uiFlags);
  return { pinned: parseNavKeys(flags.adminNavPinned), recent: parseNavKeys(flags.adminNavRecent) };
};

export const togglePinnedKey = (key, pinnedKeys) =>
  pinnedKeys.includes(key) ? pinnedKeys.filter(k => k !== key) : [...pinnedKeys, key];

// Move `key` to the front, dedupe, cap to MAX_RECENTS.
export const recordRecentKey = (key, recentKeys) => [key, ...recentKeys.filter(k => k !== key)].slice(0, MAX_RECENTS);

// Filter items by a query across label + description.
export const searchNavItems = (items, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(item => item.label.toLowerCase().includes(q) || item.description.toLowerCase().includes(q));
};

export const sortNavItemsAlphabetically = items => [...items].sort((a, b) => a.label.localeCompare(b.label));

// Resolve an ordered list of keys to their (visible) nav items.
export const keysToItems = (keys, visibleItems) => {
  const byKey = new Map(visibleItems.map(item => [item.key, item]));
  return keys.map(key => byKey.get(key)).filter(Boolean);
};

// Shortcut lists: pinned items (in pinned order) then recents (by recency, minus pinned, optionally capped).
export const getPinnedAndRecentItems = (pinnedKeys, recentKeys, visibleItems, recentLimit) => {
  const pinnedSet = new Set(pinnedKeys);
  const pinnedItems = keysToItems(pinnedKeys, visibleItems);
  const recentItems = keysToItems(recentKeys, visibleItems).filter(item => !pinnedSet.has(item.key));
  return { pinnedItems, recentItems: recentLimit == null ? recentItems : recentItems.slice(0, recentLimit) };
};

const moveItem = (arr, from, to) => {
  const next = [...arr];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
};

// The bar renders pinned-then-recent, so to make a dropped chip stay at its slot we must pin every
// chip up to and including the last pinned/dragged one, in the new order. The tail stays recent.
export const getReorderedPinnedKeys = (orderedKeys, activeId, overId, pinnedKeys) => {
  if (activeId === overId) return pinnedKeys;
  const oldIndex = orderedKeys.indexOf(activeId);
  const newIndex = orderedKeys.indexOf(overId);
  if (oldIndex === -1 || newIndex === -1) return pinnedKeys;
  const newOrder = moveItem(orderedKeys, oldIndex, newIndex);
  const pinnedSet = new Set(pinnedKeys);
  let lastPinnedIdx = -1;
  newOrder.forEach((key, i) => {
    if (pinnedSet.has(key) || key === activeId) lastPinnedIdx = i;
  });
  return newOrder.slice(0, lastPinnedIdx + 1);
};

// One-time lift of pins from the pre-server localStorage keys, so existing setups aren't lost.
const LEGACY_PINNED_KEY = 'admin_nav_pinned_keys';
const LEGACY_RECENTS_KEY = 'admin_nav_recent_keys';

export const readLegacyPinnedKeys = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LEGACY_PINNED_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const clearLegacyLocalNavPrefs = () => {
  try {
    localStorage.removeItem(LEGACY_PINNED_KEY);
    localStorage.removeItem(LEGACY_RECENTS_KEY);
  } catch {
    // ignore (private mode / unavailable storage)
  }
};
